import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderStructureType } from '@/contexts/FolderStructureVisibilityContext';
import { useToast } from '@/hooks/use-toast';
import { useTabContext } from '@/contexts/TabContext';
import { confirmation } from '@/hooks/use-confirmation';
import { ITEM_TYPE_CONFIG, normalizeBlueprintVersion } from '../config/itemTypeConfig';
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import { useDocumentDataContext } from '@/providers/DocumentDataProvider';
import { useDialogDataContext } from '@/providers/DialogDataProvider';
import { useTypesDataContext } from '@/providers/TypesDataProvider';
import { logicFlowStore, createLogicFlow, updateLogicFlow, deleteLogicFlow } from '@/lib/stores/logicFlowStore';
import { createObject, updateObject, deleteObject, duplicateTranslationKeys, duplicateDialogTranslationKeys } from '@/lib/stores/objectsStore';
import { createDocumentItem, updateDocumentItem, deleteDocumentItem } from '@/lib/stores/documentStore';
import { createTemplate, updateTemplate, deleteTemplate } from '@/lib/stores/templatesStore';
import { createDialog, updateDialog, deleteDialog } from '@/lib/stores/dialogStore';
import { createType, updateType, deleteType } from '@/lib/stores/typesStore';
import { getCurrentUser } from '@/lib/stores/unifiedUserStore';

// Store operations map for each folder type (typed as any to handle varying signatures)
const STORE_OPERATIONS: Record<FolderStructureType, { create: any; update: any; delete: any }> = {
  library: { create: createObject, update: updateObject, delete: deleteObject },
  documents: { create: createDocumentItem, update: updateDocumentItem, delete: deleteDocumentItem },
  blueprints: { create: createTemplate, update: updateTemplate, delete: deleteTemplate },
  dialogs: { create: createDialog, update: updateDialog, delete: deleteDialog },
  flows: { create: createLogicFlow, update: updateLogicFlow, delete: deleteLogicFlow },
  variables: { create: createType, update: updateType, delete: deleteType },
};

interface UseUnifiedSidebarHandlersProps {
  selectedGame: any;
}

export function useUnifiedSidebarHandlers({ selectedGame }: UseUnifiedSidebarHandlersProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { openTab } = useTabContext();
  
  // Get data contexts
  const { glossaryItems } = useGlossaryDataContext();
  const { glossaryBlueprints } = useGlossaryBlueprintsDataContext();
  const { documents } = useDocumentDataContext();
  const { dialogs } = useDialogDataContext();
  const { types } = useTypesDataContext();

  // Get items by folder type
  const getItemsForType = useCallback((folderType: FolderStructureType) => {
    switch (folderType) {
      case 'library': return glossaryItems;
      case 'blueprints': return glossaryBlueprints;
      case 'documents': return documents;
      case 'dialogs': return dialogs;
      case 'variables': return types;
      case 'flows': {
        return logicFlowStore.getData();
      }
      default: return [];
    }
  }, [glossaryItems, glossaryBlueprints, documents, dialogs, types]);

  // Generic item click handler - opens items in a NEW tab (sidebar behavior)
  // This is different from in-page navigation which stays in the same tab
  const handleItemClick = useCallback((folderType: FolderStructureType) => {
    const config = ITEM_TYPE_CONFIG[folderType];
    
    return (item: any) => {
      if (item.id) {
        const path = `${config.routes.detail}/${item.id}`;
        const itemName = item.name || 'Loading...';
        
        // Generate a unique tab ID for this item (path-based ensures uniqueness)
        const tabId = path;
        
        // Open a NEW tab for this item (sidebar always opens new tabs)
        // Use just the item name as the title
        openTab({
          id: tabId,
          title: itemName,
          path: path,
          baseTitle: itemName,
        }, {
          activate: true,
        });
        
        // Navigate to the path with item name in state so useAutoTabs can use it
        navigate(path, { state: { itemName } });
      }
    };
  }, [navigate, openTab]);

  // Generic create handlers
  const handlecreateObject = useCallback((folderId?: string) => {
    const searchParams = folderId ? `?folder=${folderId}` : '';
    navigate(`/library/new${searchParams}`);
  }, [navigate]);

  const handleCreateBlueprint = useCallback((folderId?: string) => {
    const searchParams = folderId ? `?folder=${folderId}` : '';
    navigate(`/templates/new${searchParams}`);
  }, [navigate]);

  const handleCreateDocument = useCallback((folderId?: string) => {
    // Navigate to new document page (document is only saved when user presses Create)
    const searchParams = folderId ? `?folder=${folderId}` : '';
    navigate(`/documents/new${searchParams}`);
  }, [navigate]);

  const handleCreateDialog = useCallback((folderId?: string) => {
    const searchParams = folderId ? `?folder=${folderId}` : '';
    navigate(`/dialogs/new${searchParams}`);
  }, [navigate]);

  const handleCreateFlow = useCallback(async (folderId?: string) => {
    if (!selectedGame?.id) {
      toast({
        title: 'Error',
        description: 'Please select a game first',
        variant: 'destructive',
      });
      return;
    }

    try {
      const currentUser = getCurrentUser();
      
      const newFlow = await createLogicFlow({
        game_id: selectedGame.id,
        name: 'New Logic Flow',
        description: 'Logic flow',
        data: {
          nodes: [],
          edges: [],
          viewport: { x: 0, y: 0, zoom: 1 },
        },
        folder: folderId || null,
        owner: currentUser?.id || null,
      } as any, toast);

      navigate(`/logic/${newFlow.id}`);
    } catch (error) {
      console.error('Error creating logic flow:', error);
    }
  }, [selectedGame, navigate, toast]);

  const handleCreateVariable = useCallback((folderId?: string) => {
    const searchParams = folderId ? `?folder=${folderId}` : '';
    navigate(`/variables/new${searchParams}`);
  }, [navigate]);

  // Generic rename handler
  const handleItemRename = useCallback((folderType: FolderStructureType) => {
    const items = getItemsForType(folderType);
    const storeOps = STORE_OPERATIONS[folderType];
    
    return async (itemId: string, newName: string) => {
      if (!selectedGame?.id) return;
      
      try {
        const item = items?.find((i: any) => i.id === itemId);
        if (!item) return;

        // Handle special cases
        if (folderType === 'blueprints') {
          const normalizedVersion = normalizeBlueprintVersion(item.version);
          await storeOps.update({
            ...item,
            type: item.type || 'glossary',
            name: newName,
            data: item.data as any,
            version: normalizedVersion,
          });
        } else if (folderType === 'library') {
          await storeOps.update({
            ...item,
            type: item.type || 'glossary',
            name: newName,
          }, 'patch', undefined, toast);
        } else if (folderType === 'flows') {
          await storeOps.update({
            ...item,
            name: newName,
          }, toast);
        } else if (folderType === 'variables') {
          await storeOps.update({
            ...item,
            name: newName,
            data: item.data || [],
          }, toast);
        } else {
          await storeOps.update({
            ...item,
            name: newName,
          });
        }
        
        toast({
          title: "Success",
          description: `Renamed to "${newName}"`,
        });
      } catch (error) {
        console.error('Error renaming item:', error);
        toast({
          title: "Error",
          description: "Failed to rename item",
          variant: "destructive",
        });
      }
    };
  }, [selectedGame, getItemsForType, toast]);

  // Generic delete handler with confirmation dialog
  const handleItemDelete = useCallback((folderType: FolderStructureType) => {
    const items = getItemsForType(folderType);
    const storeOps = STORE_OPERATIONS[folderType];
    const config = ITEM_TYPE_CONFIG[folderType];
    
    return async (itemId: string) => {
      if (!selectedGame?.id) return;
      
      const item = items?.find((i: any) => i.id === itemId);
      if (!item) return;

      const itemName = item.name || 'this item';
      const itemTypeLabel = config.linkEngineType.charAt(0).toUpperCase() + config.linkEngineType.slice(1);

      const confirmDialog = confirmation({
        title: `Delete ${itemTypeLabel}`,
        description: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        variant: 'destructive',
        onConfirm: async () => {
          try {
            await storeOps.delete(itemId, selectedGame.id, toast);
          } catch (error) {
            console.error('Error deleting item:', error);
            toast({
              title: "Error",
              description: "Failed to delete item",
              variant: "destructive",
            });
          }
          confirmDialog.dismiss();
        },
        onCancel: () => {
          confirmDialog.dismiss();
        },
      });
    };
  }, [selectedGame, getItemsForType, toast]);

  // Generic duplicate handler
  const handleItemDuplicate = useCallback((folderType: FolderStructureType) => {
    const config = ITEM_TYPE_CONFIG[folderType];
    const items = getItemsForType(folderType);
    const storeOps = STORE_OPERATIONS[folderType];
    
    return async (itemId: string) => {
      if (!selectedGame?.id) return;
      
      try {
        const item = items?.find((i: any) => i.id === itemId);
        if (!item) return;

        // Handle translation key duplication for glossary items
        if (folderType === 'library' && config.specialHandling?.duplicateTranslationKeys) {
          let duplicatedData = item.data;
          if (item.data && item.data.length > 0) {
            try {
              duplicatedData = await duplicateTranslationKeys(item.data, selectedGame.id, toast);
            } catch (error) {
              console.error('Error duplicating translation keys:', error);
            }
          }
          
          const duplicatedItem = {
            ...item,
            id: undefined,
            type: item.type || 'glossary',
            name: `${item.name} (Copy)`,
            blueprint_version: item.blueprint_version || 0,
            data: duplicatedData,
          };
          await storeOps.create(duplicatedItem);
        }
        // Handle blueprint duplication
        else if (folderType === 'blueprints' && config.specialHandling?.duplicateTranslationKeys) {
          const normalizedVersion = normalizeBlueprintVersion(item.version);
          
          let duplicatedData = item.data;
          if (item.data && typeof item.data === 'object') {
            const dataObj = item.data as any;
            if (Array.isArray(dataObj) && dataObj.length > 0 && dataObj[0]?.fields) {
              try {
                duplicatedData = await duplicateTranslationKeys(dataObj, selectedGame.id, toast) as any;
              } catch (error) {
                console.error('Error duplicating translation keys in blueprint:', error);
              }
            }
          }
          
          const duplicatedItem = {
            ...item,
            id: undefined,
            type: item.type || 'glossary',
            name: `${item.name} (Copy)`,
            data: duplicatedData,
            version: normalizedVersion,
          };
          await storeOps.create(duplicatedItem);
        }
        // Handle dialog duplication
        else if (folderType === 'dialogs' && config.specialHandling?.duplicateDialogKeys) {
          let duplicatedData = item.data;
          if (item.data && typeof item.data === 'object') {
            try {
              duplicatedData = await duplicateDialogTranslationKeys(item.data, selectedGame.id, toast);
            } catch (error) {
              console.error('Error duplicating translation keys in dialog:', error);
            }
          }
          
          const duplicatedItem = {
            ...item,
            id: undefined,
            name: `${item.name} (Copy)`,
            dialog_key: `${item.dialog_key}_copy_${Date.now()}`,
            data: duplicatedData,
          };
          await storeOps.create(duplicatedItem);
        }
        // Handle flow duplication
        else if (folderType === 'flows' && config.specialHandling?.requiresCurrentUser) {
          const currentUser = getCurrentUser();
          
          const duplicatedItem = {
            game_id: item.game_id,
            name: `${item.name} (Copy)`,
            description: item.description,
            data: item.data,
            folder: item.folder || null,
            owner: currentUser?.id || null,
          };
          await storeOps.create(duplicatedItem as any, toast);
        }
        // Handle variable duplication
        else if (folderType === 'variables' && config.specialHandling?.requiresElementType) {
          const duplicatedItem = {
            game_id: item.game_id,
            name: `${item.name} (Copy)`,
            element_type: item.element_type,
            group: item.group_name,
            data: item.data || [],
            description: item.description,
            folder: item.folder || undefined,
          };
          await storeOps.create(duplicatedItem, toast);
        }
        // Handle document duplication (no translation keys)
        else {
          const duplicatedItem = {
            ...item,
            id: undefined,
            name: `${item.name} (Copy)`,
          };
          await storeOps.create(duplicatedItem);
        }
        
        toast({
          title: "Success",
          description: `Duplicated "${item.name}"`,
        });
      } catch (error) {
        console.error('Error duplicating item:', error);
        toast({
          title: "Error",
          description: "Failed to duplicate item",
          variant: "destructive",
        });
      }
    };
  }, [selectedGame, getItemsForType, toast]);

  return {
    handleItemClick,
    handleItemRename,
    handleItemDelete,
    handleItemDuplicate,
    handlecreateObject,
    handleCreateDocument,
    handleCreateBlueprint,
    handleCreateDialog,
    handleCreateFlow,
    handleCreateVariable,
  };
}
